import { Fragment, useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';

import moment from 'moment';

import { Button } from 'primereact/button';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Checkbox } from 'primereact/checkbox';
import { InputNumber } from 'primereact/inputnumber';
import { Dropdown } from 'primereact/dropdown';
import { OverlayPanel } from 'primereact/overlaypanel';

import { PetrolimexLogService as Service } from './PetrolimexLogService';
import { UnitService } from '../don-vi-tinh/UnitService';
import { TaxService } from '../thue-suat/TaxService';

import { useToolbar } from '../../../shared/hooks/Toolbar';
import { useDataTable } from '../../../shared/hooks/DataTable';
import { useDialogCrup } from '../../../shared/hooks/DialogCrup';
import { dateOperations, FormatDisplay, numericOperations, stringOperations } from '../../../shared/format';
import { ConfirmDialogService } from '../../../AppConfirmDialog';
import EventEmitter from '../../../utils/EventEmitter';
import { RegisterInvoiceService } from '../../../service/invoice/RegisterInvoiceService';
import { ToastService } from '../../../AppToast';
import { DeviceService } from '../thiet-bi/DeviceService';
import { ConfigService } from '../../../service/ConfigService';

import { DialogView } from '../../hoa-don/component/DialogView';
import { DialogCrupXangDau } from '../../hoa-don/component/DialogCrupXangDau';
import { CurrencyService } from '../tien-te/CurrencyService';
import { InvoiceService } from '../../hoa-don/InvoiceService';
import { DialogSendEmail } from '../../hoa-don/component/DialogSendEmail';
import { useSign } from '../../../shared/component/Sign';
import { useImExExcel } from '../../../shared/hooks/ImExExcel';
import { ExportExcel } from './ExportExcel';
import { Row } from 'primereact/row';
import { ColumnGroup } from 'primereact/columngroup';
import { AppStorage } from '../../../service/UserStorage';
import { StoreService } from '../cua-hang-xang-dau/StoreService';
import { TenantClientService } from '../khach-hang/TenantClientService';
import { TaxCodeService } from '../../bao-cao/trang-thai-mst/TaxCodeService';
import { PetrolimexService } from '../hang-hoa-xang-dau/PetrolimexService';
import { DialogQrCode } from '../../hoa-don/component/DialogQrCode';
import {
  fetchPetrolQrPaymentEnabled,
  PETROL_QR_PAYMENT_NOT_REGISTERED_MESSAGE,
} from '../../hoa-don/hooks/petrolQrPaymentSetting';
import { DialogInvoicePaymentHistory } from '../../hoa-don/component/DialogInvoicePaymentHistory';
import { ROLE } from '../../../Constant';
import { renderListPaymentStatusBadge } from '../../lich-su-truyen-nhan/lich-su-thanh-toan/paymentLogDisplay';
import { localized, localizeOptions, translate, useTranslation } from '../../../shared/language';
import { normalizeCustomerAddress } from '../../../shared/utils/customerAddress';

export const List = () => {
  const { t } = useTranslation();

  const op = useRef(null);

  const [columnConfig, setColumnConfig] = useState(null);
  const [selectedColumns, setSelectedColumns] = useState([]);
  let userName = AppStorage.get('UserName');

  useEffect(() => {
    ConfigService.get('petrolimex').then(data => {
      if (data?.items?.length) {
        setConfig(data.items);
        setSelectedColumns(data.items);
      } else {
        loadDefaultConfig();
      }
    });
  }, []);

  const loadDefaultConfig = () => {
    ConfigService.get('revert-config-petrolimex-default').then(data => {
      setConfig(data);
    });
  }

  const setConfig = items => {
    if (items?.length) {
      setColumnConfig(items);
    }
  }

  // Cấu hình từ API giữ tên cột tiếng Việt; chỉ dịch phần hiển thị để không ghi đè giá trị gốc khi lưu.
  const columnConfigSum = (columnConfig || [])
    .filter(item => item.dataType === 'numeric')
    .map(item => ({ value: item.field, label: translate(item.header) }));

  const onRowReorder = e => {
    setColumnConfig(e.value);
  }

  const onColumnConfigChange = (rowIndex, field, value) => {
    const _columnConfig = [...columnConfig];
    _columnConfig[rowIndex][field] = value;
    setColumnConfig(_columnConfig);
  }

  const saveColumnConfig = () => {
    ConfigService.set('petrolimex', {
      totalCount: 0,
      detail: columnConfig
    }).then(() => {
      setSelectedColumns(columnConfig);
      op.current.hide();
    });
  }

  const configButton = <Fragment>
    <Button icon="pi pi-cog" className="p-button-rounded p-button-outlined p-button-secondary ml-1" onClick={e => op.current.toggle(e)} />
    <OverlayPanel ref={op} style={{ width: '70rem', maxWidth: '100vw' }}>
      <DataTable value={columnConfig} reorderableRows onRowReorder={onRowReorder}
        scrollable scrollHeight="50vh" responsiveLayout="scroll" className="p-fluid" size={'small'}>
        <Column body={(rowData, { rowIndex }) =>
          <Checkbox checked={rowData.active} onChange={e => onColumnConfigChange(rowIndex, 'active', e.checked)} />}
          style={{ minWidth: '2em', maxWidth: '2em' }} />
        <Column field="header" header={t('common.columnName')} body={rowData => translate(rowData.header)} />
        <Column field="width" header={t('common.columnWidth')} body={(rowData, { rowIndex }) =>
          <InputNumber locale={FormatDisplay.locales} value={rowData.width} min={10} max={500} onValueChange={e => onColumnConfigChange(rowIndex, 'width', e.value)} />}
          style={{ minWidth: '8em', maxWidth: '8em' }} />
        <Column field="matchMode" header={t('common.filter')} body={(rowData, { rowIndex }) => {
          const options = [];
          switch (rowData.dataType) {
            case 'string':
              options.push(...stringOperations);
              break;
            case 'numeric':
              options.push(...numericOperations);
              break;
            case 'date':
            case 'dateTime':
              options.push(...dateOperations);
              break;
            case 'boolean':
              options.push({ value: '=', label: t('common.equals') });
              break;
          }
          // Dropdown của PrimeReact không tự chuyển translationKey thành label để hiển thị.
          return <Dropdown value={rowData.matchMode} options={localizeOptions(options)} onChange={e => onColumnConfigChange(rowIndex, 'matchMode', e.value)} showClear style={{ width: '100%' }} />
        }
        } style={{ minWidth: '17em', maxWidth: '17em' }} />
        <Column field="sum" header={t('common.total')} body={(rowData, { rowIndex }) =>
          <Dropdown value={rowData.sum} options={columnConfigSum} onChange={e => onColumnConfigChange(rowIndex, 'sum', e.value)} showClear style={{ width: '100%' }} />}
          style={{ minWidth: '18em', maxWidth: '18em' }} />
        <Column rowReorder style={{ minWidth: '3em', maxWidth: '3em' }} />
      </DataTable>
      <div className="text-right mt-4">
        <Button label={t('common.restoreDefault')} className="p-button-text p-button-sm mr-2" onClick={loadDefaultConfig} />
        <Button label={t('common.save')} icon="pi pi-check" className="p-button-sm" onClick={saveColumnConfig} />
      </div>
    </OverlayPanel>
  </Fragment>

  return <Screen selectedColumns={selectedColumns} configButton={configButton} />
}

const getDefaultMatchMode = (dataType: string) => {
  switch (dataType) {
    case 'numeric':
      return '=';
    case 'date':
    case 'dateTime':
      return '>=';
    case 'boolean':
      return '=';
    default:
      return 'LIKE';
  }
};

const Screen = ({ selectedColumns, configButton }) => {
  const { t } = useTranslation();

  const header = t('catalog.petrolTransactionList');
  const dataKey = 'id';

  const op = useRef(null);

  const [columns, setColumns] = useState([]);
  const [columnsState, setColumnsState] = useState(0);
  const [footerColumnGroup, setFooterColumnGroup] = useState(null);

  const [units, setUnits] = useState([]);
  const [taxs, setTaxs] = useState([]);
  const [manyActions, setManyActions] = useState([]);

  const [displayCrupXangDau, setDisplayCrupXangDau] = useState(null);
  const [createDefaultValue, setCreateDefaultValue] = useState(null);
  const [currencies, setCurrencies] = useState(null);
  const [paymentTypes, setPaymentTypes] = useState(null);
  const [registerInvoice, setRegisterInvoice] = useState(null);
  const [registerInvoices, setRegisterInvoices] = useState([]);

  const [displayView, setDisplayView] = useState(false);
  const [viewItem, setViewItem] = useState(false);

  const [displaySendEmail, setDisplaySendEmail] = useState(false);
  const [returnInvoiceScreen, setReturnInvoiceScreen] = useState(null);

  const [displayExportExcel, setDisplayExportExcel] = useState(null);
  const [displayQrCode, setDisplayQrCode] = useState(null);
  const [displayPaymentHistory, setDisplayPaymentHistory] = useState(false);
  const [paymentHistoryItem, setPaymentHistoryItem] = useState(null);
  const [selectedRowsCount, setSelectedRowsCount] = useState(0);
  const [selectedQuantity, setSelectedQuantity] = useState(0);
  const [selectedTotalAmount, setSelectedTotalAmount] = useState(0);

  const [formHeader, setFormHeader] = useState(header);

  const [storeCodeOptions, setStoreCodeOptions] = useState([]);
  const [deviceCodeOptions, setDeviceCodeOptions] = useState([]);
  const [productCodeOptions, setProductCodeOptions] = useState([]);
  const [isFormCreate, setIsFormCreate] = useState(false)

  let userName = AppStorage.get('UserName');
  const permissionString = AppStorage.get('pk');
  const permissions = JSON.parse(permissionString || '[]');
  const canViewPaymentHistory = permissions.includes(ROLE.MGATEWAY_INVOICE_SUMMARY);

  const showPaymentHistory = useCallback((rowData) => {
    setPaymentHistoryItem(rowData);
    setDisplayPaymentHistory(true);
  }, []);

  useEffect(() => {
    RegisterInvoiceService.getUsingList({ maxResultCount: 1000, userName, Sorting: 'creationTime', SortType: 'DESCEND' }).then(({ items }) => {
      const _registerInvoices = [];
      items.forEach(item => {
        item.label = `${item.symbolCode} - ${item.invoiceTypeName}`;
        _registerInvoices.push(item);
      });
      setRegisterInvoices(_registerInvoices);
    });

    UnitService.getList({
      skipCount: 0,
      maxResultCount: 1000,
    }).then(({ items }) => {
      const _units = [];
      items.forEach(item => {
        _units.push({ value: item.id, label: item.name });
      });
      setUnits(_units);
    });
    TaxService.getList({ maxResultCount: 1000 }).then(({ items }) => {
      const _taxs = [];
      items.forEach((item) => {
        _taxs.push({ value: item.id, label: item.name, ratio: item.ratio });
      });
      setTaxs(_taxs);
    });
    CurrencyService.getList({ maxResultCount: 1000 }).then(({ items }) => {
      setCurrencies(items);
    });
    InvoiceService.getPaymentTypes().then(({ items }) => {
      const _paymentTypes = [];
      items.forEach(item => {
        _paymentTypes.push({ value: item.name, label: item.name });
      });
      setPaymentTypes(_paymentTypes);
    });

    StoreService.getList({ maxResultCount: 1000 }).then(({ items }) => {
      setStoreCodeOptions(items.map(item => {
        item.storeNameDisplay = `${item.code} - ${item.storeName}`
        return { ...item };
      }));
    });
  }, []);

  useEffect(() => {
    if (selectedColumns?.length) {
      setColumns(selectedColumns.filter(item => item.active).map(item => {
        const opts: any = {};
        const matchMode = item.matchMode || getDefaultMatchMode(item.dataType);
        switch (item.dataType) {
          case 'date':
            opts.dataType = 'date';
            opts.filterType = 'date-range';
            opts.showFilterMenu = false;
            break;
          case 'dateTime':
            opts.dataType = 'date-time';
            opts.filterType = 'date-time-range';
            opts.showFilterMenu = false;
            break;
          case 'numeric':
            opts.dataType = 'custom';
            opts.customCell = rowData => {
              return FormatDisplay.number(rowData[item.field], { maximumSignificantDigits: `${rowData[item.field]}`.length });
            }
            opts.style = { justifyContent: 'end' };
            opts.filterType = 'number';
            opts.filterProps = { maxFractionDigits: 6 };
            opts.showFilterMenu = true;
            opts.filterMatchModeOptions = numericOperations;
            break;
          case 'boolean':
            opts.dataType = 'boolean';
            opts.filterType = 'boolean';
            opts.showFilterMenu = true;
            break;
          default:
            opts.showFilterMenu = true;
            opts.filterMatchModeOptions = stringOperations;
        }
        switch (item.field) {
          case 'invoiceSerial':
            opts.dataType = 'custom';
            opts.customCell = rowData => {
              return <Link to={`/hoa-don?invoiceSerial=${rowData.invoiceSerial}`}>{rowData.invoiceSerial}</Link>
            }
            break;
          case 'invoiceNo':
            opts.dataType = 'custom';
            opts.customCell = rowData => {
              if (rowData.invoiceNo) {
                return <span onClick={() => onInvoiceNoClick(rowData)} style={{ color: 'blue', textDecoration: 'underline', cursor: 'pointer' }}>{rowData.invoiceNo}</span>
              }
              return '';
            }
            break;
          case 'paymentStatus':
            opts.width = 150;
            opts.minWidth = 150;
            opts.dataType = 'custom';
            opts.style = { justifyContent: 'center' };
            opts.filterType = undefined;
            opts.matchMode = undefined;
            opts.showFilterMenu = false;
            opts.customCell = rowData => {
              const badge = renderListPaymentStatusBadge(rowData.paymentStatus);

              if (!badge) {
                return null;
              }

              if (!canViewPaymentHistory) {
                return badge;
              }

              return (
                <span
                  className="cursor-pointer"
                  onClick={() => showPaymentHistory(rowData)}
                >
                  {badge}
                </span>
              );
            };
            break;
          case 'isPaymented':
            opts.width = 150;
            opts.minWidth = 150;
            opts.dataType = 'custom';
            opts.style = { justifyContent: 'center' };
            opts.filterType = undefined;
            opts.matchMode = undefined;
            opts.showFilterMenu = false;
            opts.customCell = rowData => {
              const badge = renderListPaymentStatusBadge(rowData.paymentStatus);

              if (!badge) {
                return null;
              }

              if (!canViewPaymentHistory) {
                return badge;
              }

              return (
                <span
                  className="cursor-pointer"
                  onClick={() => showPaymentHistory(rowData)}
                >
                  {badge}
                </span>
              );
            };
            break;
        }
        return {
          field: item.field,
          header: item.header,
          width: item.width,
          minWidth: item.minWidth,
          matchMode,
          sum: item.sum,
          ...opts,
        };
      }));
      setColumnsState(Date.now);
    }
  }, [selectedColumns, registerInvoices, canViewPaymentHistory, showPaymentHistory]);

  useEffect(() => {
    const eventsChangeSerial = EventEmitter.addListener("changeTaxs", changeTaxs);
    return () => {
      eventsChangeSerial.removeListener("changeTaxs", changeTaxs);
    }
  });

  const onInvoiceNoClick = rowData => {
    if (rowData?.invoiceId) {
      const _registerInvoice = registerInvoices.find(it => it.id === rowData.registerInvoiceId);
      if (_registerInvoice) {
        setRegisterInvoice(_registerInvoice);
        InvoiceService.getDetail(rowData.invoiceId).then(data => {
          if (data?.sendTaxStatus === 1) {
            setReturnInvoiceScreen(data.invoiceSerial);
            setCreateDefaultValue(data);
            setDisplayCrupXangDau('update');
          } else {
            view(data);
          }
        });
      }
    }
  }

  const changeTaxs = (changeTaxsData) => {
  }

  const { render: renderDataTable, refreshLazyData, reloadLazyData, items, selectedItems, showSelection, dataTableParams } = useDataTable({
    header,
    dataKey,
    columns,
    columnsState,
    footerColumnGroup,
    indexColumnWidth: 40,
    actionColumnWidth: 80,
    getList: (params, lazyParams) => {
      const postBody: any = {
        skipCount: params.skipCount,
        userName,
        maxResultCount: params.maxResultCount,
        sorting: params.sorting,
        sortType: params.sortType,
        condition: []
      };
      let invalid = false;
      if (lazyParams?.filters) {
        Object.keys(lazyParams.filters).forEach(k => {
          switch (lazyParams.filters[k].matchMode) {
            case 'HasValue':
            case 'NotHasValue':
              postBody.condition.push({
                propertyName: k,
                operation: lazyParams.filters[k]?.matchMode
              });
              break;
            default:
              let value = lazyParams.filters[k]?.value;
              if (value instanceof Date) {
                value = FormatDisplay.date(value, 'YYYY-MM-DDTHH:mm:ss')
              }
              if (typeof value === 'string') {
                value = value.trim();
              }
              if (value !== undefined && value !== null && value !== '') {
                const _field = selectedColumns?.find(it => it.field === k);
                switch (_field?.dataType) {
                  case 'date':
                  case 'dateTime':
                    if (value?.split(' - ')?.length === 2) {
                      const fromDate = moment(value?.split(' - ')[0], 'DD/MM/YYYY HH:mm:ss');
                      const toDate = moment(value?.split(' - ')[1], 'DD/MM/YYYY HH:mm:ss');
                      if (!fromDate.isValid()) {
                        ToastService.error(localized('validation.fromDateInvalid'));
                        invalid = true;
                      }
                      if (!toDate.isValid()) {
                        ToastService.error(localized('validation.toDateInvalid'));
                        invalid = true;
                      }
                      if (toDate.isBefore(fromDate)) {
                        ToastService.error(localized('validation.dateRangeOrder'));
                        invalid = true;
                      }
                      postBody.condition.push({
                        propertyName: k,
                        operation: '>=',
                        value: FormatDisplay.date(fromDate.toDate(), 'YYYY-MM-DDTHH:mm:ss')
                      });
                      postBody.condition.push({
                        condition: 'And',
                        propertyName: k,
                        operation: '<=',
                        value: FormatDisplay.date(toDate.toDate(), 'YYYY-MM-DDTHH:mm:ss')
                      });
                    }
                    break;
                  default:
                    postBody.condition.push({
                      propertyName: k,
                      operation: lazyParams.filters[k]?.matchMode,
                      value
                    });
                }
              }
          }
        });
      }
      if (invalid) {
        return Promise.resolve({});
      }
      return Service.search(postBody);
    },
    initActions: (items) => {
      if (items) {
        items.forEach(item => {
          item.actions = [
            { icon: 'pi pi-file', className: '', title: t('invoice.printInvoice'), command: () => printLog(item) },
            { icon: 'pi pi-replay', className: 'p-button-warning', title: t('common.updateInvoiceNumber'), command: () => updateInvoiceNo(item) }
          ];
        });
      }
    }
  });

  useEffect(() => {
    let _footerColumnGroup = null;
    if (columns?.length) {
      let hasSumCol = false;
      for (const column of columns) {
        if (column.sum) {
          hasSumCol = true;
          if (items) {
            let sumValue = 0;
            items.forEach(item => {
              if (typeof item[column.sum] === 'number' && !Number.isNaN(item[column.sum])) {
                sumValue += item[column.sum];
              }
            });
            column.sumValue = sumValue;
          }
        }
      }
      if (hasSumCol) {
        let f_title = 80, title_stop = 0;
        columns.forEach((_column, index) => {
          if (['quantity', 'totalAmount'].includes(_column.field)) {
            if (title_stop === 0) {
              title_stop = index;
            }
          }
          if (title_stop === 0) {
            f_title += _column.width;
          }
        });
        const selectedRowView = (<Row>
          <Column footer={`Đã chọn ${FormatDisplay.number(selectedRowsCount)} bản ghi`} style={{ minWidth: `${f_title}px`, maxWidth: `${f_title}px`, border: 0, borderLeft: '1px solid #dee2e6', borderBottom: '1px solid #dee2e6', backgroundColor: '#ccc' }} />
          {
            columns.map((_column, index) => {
              if (index < title_stop) {
                return;
              }
              switch (_column.field) {
                case 'quantity':
                  return <Column key={index} footer={FormatDisplay.number(selectedQuantity)} style={{ justifyContent: 'end', minWidth: `${_column.width}px`, maxWidth: `${_column.width}px`, backgroundColor: '#ccc' }} />
                case 'totalAmount':
                  return <Column key={index} footer={FormatDisplay.number(selectedTotalAmount)} style={{ justifyContent: 'end', minWidth: `${_column.width}px`, maxWidth: `${_column.width}px`, backgroundColor: '#ccc' }} />
                default:
                  return <Column key={index} style={{ minWidth: `${_column.width}px`, maxWidth: `${_column.width}px`, border: 0, borderLeft: '1px solid #dee2e6', borderBottom: '1px solid #dee2e6', backgroundColor: '#ccc' }} />
              }
            })
          }
        </Row>);
        _footerColumnGroup = <ColumnGroup>
          {showSelection && selectedRowView}
          <Row>
            {showSelection && <Column style={{ minWidth: '40px', maxWidth: '40px', border: 0 }} />}
            <Column style={{ minWidth: '40px', maxWidth: '40px', border: 0 }} />
            {
              columns.map((_column, index) => {
                if (_column.sum) {
                  return <Column key={index} footer={FormatDisplay.number(_column.sumValue)} style={{ justifyContent: 'end', minWidth: `${_column.width}px`, maxWidth: `${_column.width}px` }} />
                }
                return <Column key={index} style={{ minWidth: `${_column.width}px`, maxWidth: `${_column.width}px`, border: 0, borderLeft: '1px solid #dee2e6' }} />
              })
            }
          </Row>
        </ColumnGroup>
      }
    }
    setFooterColumnGroup(_footerColumnGroup);
  }, [columns, items, showSelection, selectedRowsCount]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    refreshToolbar();

    let _selectedRowsCount = 0;
    let _selectedQuantity = 0;
    let _selectedTotalAmount = 0;
    if (selectedItems?.length) {
      _selectedRowsCount = selectedItems.length;
      selectedItems.forEach(si => {
        if (typeof si.quantity === 'number' && !Number.isNaN(si.quantity)) {
          _selectedQuantity += si.quantity;
        }
        if (typeof si.totalAmount === 'number' && !Number.isNaN(si.totalAmount)) {
          _selectedTotalAmount += si.totalAmount;
        }
      });
    }
    setSelectedRowsCount(_selectedRowsCount);
    setSelectedQuantity(_selectedQuantity);
    setSelectedTotalAmount(_selectedTotalAmount);
  }, [selectedItems]); // eslint-disable-line react-hooks/exhaustive-deps

  const { render: renderDialogCrup, create, update, clone, getValue, setValue, touched } = useDialogCrup({
    header: formHeader,
    dataKey,
    width: '60rem',
    fields: [
      { field: 'offset', type: 'hidden' },
      { field: 'keyApi', type: 'hidden' },
      { field: 'saleDate', header: t('catalog.saleDateTime'), required: true, type: 'Calendar', CalendarProps: { showTime: true, showSeconds: true }, className: 'md:col-6' },
      { field: 'storeCode', header: t('catalog.storeCode'), required: true, type: 'Dropdown', DropdownProps: { options: storeCodeOptions, optionLabel: 'storeNameDisplay', filter: true }, className: 'md:col-6' },
      { field: 'deviceCode', header: t('catalog.pumpCode'), required: true, type: 'Dropdown', DropdownProps: { options: deviceCodeOptions, filter: true }, className: 'md:col-6' },
      { field: 'productCode', header: t('catalog.productCode'), required: true, type: 'Dropdown', DropdownProps: { options: productCodeOptions, filter: true }, className: 'md:col-6' },
      { field: 'quantity', header: t('common.quantity'), required: true, type: 'InputNumber', InputNumberProps: { min: 0, maxFractionDigits: 6 }, className: 'md:col-3' },
      { field: 'price', header: t('common.unitPrice'), required: true, type: 'InputNumber', InputNumberProps: { min: 0, maxFractionDigits: 6 }, className: 'md:col-3' },
      { field: 'totalAmount', header: t('common.totalPayment'), required: true, type: 'InputNumber', InputNumberProps: { min: 0, maxFractionDigits: 6 }, className: 'md:col-6' },
      { field: 'actionCode', header: t('catalog.pumpActionCode'), InputTextProps: { maxLength: 500 }, className: 'md:col-6' },
      { field: 'buyerCode', header: t('common.customerCode'), InputTextProps: { maxLength: 500 }, className: 'md:col-6' },
      { field: 'beginTime', header: t('catalog.pumpStartTime'), type: 'Calendar', CalendarProps: { showTime: true, showSeconds: true }, className: 'md:col-6' },
      { field: 'totalCount', header: t('catalog.pumpedTotal'), type: 'InputNumber', InputNumberProps: { min: 0, maxFractionDigits: 6 }, className: 'md:col-6' },
      { field: 'staffCode', header: t('catalog.salesStaffCode'), InputTextProps: { maxLength: 500 }, className: 'md:col-6' },
      { field: 'fieldNumber1', header: t('catalog.previousPumpedTotal'), type: 'InputNumber', InputNumberProps: { min: 0, maxFractionDigits: 6 }, className: 'md:col-6' }
    ],
    createItem: (item) => {
      item.storeCode = item.storeCode.code;

      return Service.create(item).then(result => {
        if (item.offset) {
          return Service.logOffset(item.offset).then(() => {
            return result;
          });
        }
        return result;
      });
    },
    updateItem: (id, item) => {
      item.storeCode = item.storeCode.code;
      return Service.update(id, item);
    },
    refreshLazyData: () => {
      setFormHeader(header);
      refreshLazyData();
    }
  });

  const { storeCode, deviceCode, taxId, listPrice, quantity, price, totalAmount, inputChange } = getValue();

  useEffect(() => {
    if (storeCode?.code) {
      DeviceService.getList({ maxResultCount: 1000, storeCode: storeCode.code }).then(({ items }) => {
        const _deviceCodeOptions = [];
        for (const item of items) {
          _deviceCodeOptions.push({ ...item, value: item.code, label: `${item.code} [Cửa hàng ${storeCode.storeName}]` });
        }
        setDeviceCodeOptions(_deviceCodeOptions);
      });
    } else {
      setDeviceCodeOptions([]);
    }
  }, [storeCode?.code]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (deviceCodeOptions?.length) {
      PetrolimexService.getList({ maxResultCount: 1000 }).then(({ items }) => {

        let deviceCodeOption;
        let price;



        if (deviceCode) {
          deviceCodeOption = deviceCodeOptions.find(it => it.value === deviceCode);

        }
        if (deviceCodeOption?.productCode) {
          setProductCodeOptions([{ value: deviceCodeOption?.productCode, label: deviceCodeOption?.productCode }]);

          price = items.find(it => it.code === deviceCodeOption?.productCode).priceWithVat;

        } else {
          setProductCodeOptions([]);
        }

        const _value = getValue();
        if (isFormCreate) _value.price = price;

        _value.productCode = deviceCodeOption?.productCode;


        setValue(_value);
      });

    }
  }, [deviceCodeOptions, deviceCode]); // eslint-disable-line react-hooks/exhaustive-deps


  useEffect(() => {
    if (touched) {
      const _value = getValue();
      _value.price = 0;
      if (listPrice > 0) {
        _value.price = listPrice;
      }
      if (taxId) {
        const ratio = taxs.find(it => it.value === taxId)?.ratio;
        if (listPrice > 0 && ratio > 0) {
          _value.price = formatNumber(listPrice / (1 + ratio / 100));
        }
      }
      setValue(_value);
    }
  }, [taxId, listPrice]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (touched) {
      const _value = getValue();
      switch (inputChange) {
        case 'quantity':
          _value.totalAmount = 0;
          if (_value.quantity >= 0 && _value.price >= 0) {
            _value.totalAmount = formatNumber(_value.quantity * _value.price, 0);
          }
          setValue(_value);
          break;
        case 'price':
          _value.totalAmount = 0;
          if (_value.quantity >= 0 && _value.price >= 0) {
            _value.totalAmount = formatNumber(_value.quantity * _value.price, 0);
          }
          setValue(_value);
          break;
        case 'totalAmount':
          _value.quantity = 0;
          if (_value.totalAmount >= 0 && _value.price >= 0) {
            _value.quantity = formatNumber(_value.totalAmount / _value.price, 3);
          }
          setValue(_value);
          break;
      }
    }
  }, [quantity, price, totalAmount]); // eslint-disable-line react-hooks/exhaustive-deps

  const formatNumber = (number: any, fraction?: number) => {
    if (typeof number === 'number') {
      if (!Number.isFinite(number) || Number.isNaN(number)) {
        number = 0;
      }
      if (`${number}`.length > 2 && `${number}`.split('.').length) {
        number = 1 * number.toPrecision(`${number}`.split('.')[0].length + fraction || 2);
      }
    }
    return number;
  }

  useEffect(() => {
    if (!displayCrupXangDau) {
      setRegisterInvoice(null);
      setCreateDefaultValue(null);

      if (returnInvoiceScreen) {
        window.location.hash = '/hoa-don?invoiceSerial=' + returnInvoiceScreen;
      }
    }
  }, [displayCrupXangDau]);

  useEffect(() => {
    if (registerInvoice && createDefaultValue && displayCrupXangDau === 'should-create') {
      setReturnInvoiceScreen(null);
      setDisplayCrupXangDau('create');
    }
  }, [registerInvoice, createDefaultValue, displayCrupXangDau]); // eslint-disable-line react-hooks/exhaustive-deps

  const view = (item) => {
    setViewItem(item);
    setDisplayView(true);
  }

  const createInvoice = async () => {
    if (selectedItems?.['id']) {
      const selectedItem: any = selectedItems;

      const postBody: any = {
        skipCount: 0,
        maxResultCount: 10,
        condition: [{
          propertyName: 'id',
          operation: '=',
          value: selectedItem.id
        }]
      };
      const _selectedItem = await Service.search(postBody).then(({ items }) => items[0]);

      if (_selectedItem.status === 1) {
        return ToastService.error(localized('validation.invoiceAlreadyCreated'));
      } else if (_selectedItem.status === 2) {
        return ToastService.error(localized('validation.invoiceCreationFailed'));
      } else if (_selectedItem.status === 3) {
        return ToastService.error(localized('validation.invoiceCancelledCannotCreate'));
      }
      if (_selectedItem.invoiceSerial || _selectedItem.invoiceNo) {
        ToastService.error(localized('validation.invoiceAlreadyCreated'));
      } else if (_selectedItem.storeCode && _selectedItem.deviceCode) {
        DeviceService.getList({ maxResultCount: 50, skipCount: 0, code: _selectedItem.deviceCode, storeCode: _selectedItem.storeCode }).then(async result => {
          if (result?.items?.length) {
            const item = result.items[0];
            const _registerInvoice = registerInvoices.find(it => it.id === item.registerInvoiceId);
            if (_registerInvoice) {
              setRegisterInvoice(_registerInvoice);
              const buyer: any = {};
              let buyerTel = _selectedItem.fieldName2, buyerEmail = _selectedItem.fieldName3;
              if (_selectedItem.fieldName1) {
                await TenantClientService.getList({ taxCode: _selectedItem.fieldName1, MaxResultCount: 2 }).then(async tenantClientData => {
                  if (tenantClientData?.items?.length) {
                    const tenantClient = tenantClientData.items[0];
                    Object.assign(buyer, {
                      buyerTaxCode: tenantClient.taxCode,
                      buyerCode: tenantClient.code,
                      buyerLegalName: tenantClient.name,
                      buyerAddress: tenantClient.address
                    });
                    if (!buyerTel && tenantClient.mobile) {
                      buyerTel = tenantClient.mobile;
                    }
                    if (!buyerEmail && tenantClient.email) {
                      buyerEmail = tenantClient.email;
                    }
                  } else {
                    await TaxCodeService.get(_selectedItem.fieldName1).then(taxCodeData => {
                      if (taxCodeData?.ma_so_thue) {
                        Object.assign(buyer, {
                          buyerTaxCode: taxCodeData.ma_so_thue,
                          buyerLegalName: taxCodeData.ten_cty,
                          buyerAddress: normalizeCustomerAddress(taxCodeData.dia_chi)
                        });
                      } else {
                        Object.assign(buyer, {
                          buyerTaxCode: _selectedItem.fieldName1
                        });
                      }
                    });
                  }
                });
              }
              setCreateDefaultValue({
                keyApi: _selectedItem.keyApi,
                invoiceDate: _selectedItem.saleDate,
                storeName: item.storeName,
                deviceCode: item.code,
                productCode: item.productCode,
                productName: item.productName,
                unitCode: item.unitCode,
                totalAmount: _selectedItem.totalAmount,
                quantity: _selectedItem.quantity,
                unitPriceDisplay: _selectedItem.price,
                buyerTel: buyerTel,
                buyerEmail: buyerEmail,
                buyerTaxCode: buyer.buyerTaxCode,
                buyerCode: buyer.buyerCode,
                buyerLegalName: buyer.buyerLegalName,
                buyerAddress: buyer.buyerAddress,
                useBuyer: true
              });
              setDisplayCrupXangDau('should-create');
            }
          } else {
            ToastService.error(localized('validation.noData'));
          }
        });
      }
    } else if (Array.isArray(selectedItems)) {
      if (selectedItems.length) {

        const postBody: any = {
          skipCount: 0,
          maxResultCount: selectedItems.length,
          condition: [{
            propertyName: 'id',
            operation: 'LIKEArray',
            value: selectedItems.map(it => it.id).join(',')
          }]
        };
        const _selectedItems = await Service.search(postBody).then(({ items }) => items);

        if (_selectedItems.filter(it => it.invoiceSerial || it.invoiceNo).length) {
          return ToastService.error(localized('validation.containsCreatedInvoice'));
        }
        if (_selectedItems.filter(it => it.status === 1).length) {
          return ToastService.error(localized('validation.invoiceAlreadyCreated'));
        } else if (_selectedItems.filter(it => it.status === 2).length) {
          return ToastService.error(localized('validation.invoiceCreationFailed'));
        } else if (_selectedItems.filter(it => it.status === 2).length) {
          return ToastService.error(localized('validation.invoiceCancelledCannotCreate'));
        }
        ConfirmDialogService.confirm(localized('common.confirmation'), localized('validation.createInvoiceQuestion'), () => {
          const items = selectedItems.map(({ id }) => id);
          Service.createInvoice({ items }).then(() => {
            ToastService.success();
            reloadLazyData();
          });
        });
      } else {
        ToastService.error(localized('validation.selectTransaction'));
      }
    }
  }

  const printLog = (item?: any) => {
    const printItems = [];
    if (item?.id) {
      printItems.push(item['id']);
    } else if (Array.isArray(selectedItems)) {
      printItems.push(...selectedItems.map(({ id }) => id));
    } else if (selectedItems?.['id']) {
      printItems.push(selectedItems['id']);
    }
    if (printItems.length) {
      Service.printLog(printItems).then(result => {
        if (result?.pagehmtl?.length) {
          const myWindow = window.open();
          myWindow.document.open('text/plain');
          myWindow.document.write(result.pagehmtl.map(html => html.trim()).join(''));
          myWindow.document.close();
          myWindow.onafterprint = myWindow.close;
          myWindow.print();
        }
      });
    } else {
      ToastService.error(localized('validation.selectTransaction'));
    }
  }

  const updateInvoiceNo = (item?: any) => {
    const _items = [];
    if (item?.id) {
      _items.push(item);
    } else if (Array.isArray(selectedItems)) {
      _items.push(...selectedItems);
    } else if (selectedItems?.['id']) {
      _items.push(selectedItems);
    }
    for (const _item of _items) {
      if (!_item.invoiceSerial) {
        return ToastService.error(localized('validation.containsUncreatedInvoice'));
      }
    }

    if (_items.length) {
      ConfirmDialogService.confirm(localized('common.confirmation'), localized('validation.updateInvoiceNumberQuestion', { count: _items.length }), () => {
        Service.updateInvoiceNo({
          skipCount: 0, maxResultCount: (dataTableParams as any)?.maxResultCount || 50, condition: [
            {
              propertyName: 'id',
              operation: 'LIKEArray',
              value: _items.map(({ id }) => id).join(',')
            }
          ]
        }).then(() => {
          ToastService.success();
          reloadLazyData();
        });
      });
    } else {
      ToastService.error(localized('validation.selectTransaction'));
    }
  }

  const createGroupLog = () => {
    if (selectedItems?.['id']) {
      ToastService.error(localized('validation.selectMultipleTransactions'));

    } else if (Array.isArray(selectedItems)) {
      if (selectedItems.length) {
        if (selectedItems.filter(it => it.invoiceSerial || it.invoiceNo).length) {
          return ToastService.error(localized('validation.containsCreatedInvoice'));
        }
        ConfirmDialogService.confirm(localized('common.confirmation'), localized('validation.groupTransactionQuestion'), () => {
          const items = selectedItems.map(({ id }) => id);
          Service.createGroupLog({ items }).then(() => {
            ToastService.success();
            reloadLazyData();
          });
        });

        setIsFormCreate(false)
      } else {
        ToastService.error(localized('validation.selectTransaction'));
      }
    }
  }

  const doCreate = () => {
    create({
      saleDate: new Date(),
      storeCode: storeCodeOptions[0]
    });

    setIsFormCreate(true)
  }

  const updateItem = (item) => {
    if (item && item[dataKey]) {
      if (item.status === 3) {
        return ToastService.error(localized('validation.cancelledCannotEdit'));
      }
      Service.get(item[dataKey]).then(data => {
        if (data.price > 0) {
          data.listPrice = data.price;
        }
        if (data.ratioTax > 0 && data.price > 0) {
          data.listPrice = Math.round(data.price * (1 + data.ratioTax / 100));
        }
        data.storeCode = storeCodeOptions.find(it => it.code === data.storeCode);
        update(data);

        setIsFormCreate(false)
      });
    } else {
      ToastService.error(localized('validation.selectTransaction'));
    }
  }

  const createCompensation = () => {
    if (Array.isArray(selectedItems) && selectedItems?.length) {
      for (const selectedItem of selectedItems) {
        if (selectedItem?.['fieldNumber2'] > 0) {
          if (!selectedItem['fieldNumber1']) {
            return ToastService.error(localized('validation.previousVolumeInvalid'));
          }
        } else {
          return ToastService.error(localized('validation.positiveDifferenceOnly'));
        }
        if (selectedItem.isOffset) {
          return ToastService.error(localized('validation.alreadyCompensated'));
        }
      }
      ConfirmDialogService.confirm(localized('common.confirmation'), localized('validation.compensateTransactionQuestion', { count: selectedItems.length }), async () => {
        for (const selectedItem of selectedItems) {
          await Service.get(selectedItem[dataKey]).then(data => {
            if (data.price > 0) {
              data.listPrice = data.price;
            }
            if (data.ratioTax > 0 && data.price > 0) {
              data.listPrice = Math.round(data.price * (1 + data.ratioTax / 100));
            }
            return Service.create({
              storeCode: data.storeCode,
              deviceCode: data.deviceCode,
              productCode: data.productCode,
              price: data.price,
              listPrice: data.listPrice,
              saleDate: new Date(Date.now() - 1000 * 60),
              quantity: selectedItem['fieldNumber2'],
              totalAmount: Math.round((selectedItem['fieldNumber2'] * data.price) / 1000) * 1000,
              actionCode: '',
              buyerCode: '',
              beginTime: new Date(Date.now() - 1000 * 60),
              totalCount: (selectedItem['fieldNumber1'] || 0) + selectedItem['fieldNumber2'],
              fieldNumber1: selectedItem['fieldNumber1'],
              keyApi: `${selectedItem.keyApi}_Copy`
            }).then(() => {
              return Service.logOffset(selectedItem.id);
            })
          });
        }
        ToastService.success();
        reloadLazyData();
      });
    } else if (selectedItems?.[dataKey]) {
      const selectedItem: any = selectedItems;
      if (selectedItem?.['fieldNumber2'] > 0) {
        if (!selectedItem['fieldNumber1']) {
          return ToastService.error(localized('validation.previousVolumeInvalid'));
        }
        if (selectedItem.isOffset) {
          return ToastService.error(localized('validation.alreadyCompensated'));
        }
        Service.get(selectedItem[dataKey]).then(data => {
          if (data.price > 0) {
            data.listPrice = data.price;
          }
          if (data.ratioTax > 0 && data.price > 0) {
            data.listPrice = Math.round(data.price * (1 + data.ratioTax / 100));
          }
          setFormHeader('!' + t('common.compensateTransaction') + ' ' + t('common.missing'));
          create({
            storeCode: data.storeCode,
            deviceCode: data.deviceCode,
            productCode: data.productCode,
            price: data.price,
            listPrice: data.listPrice,
            saleDate: new Date(Date.now() - 1000 * 60),
            quantity: selectedItem['fieldNumber2'],
            totalAmount: Math.round((selectedItem['fieldNumber2'] * data.price) / 1000) * 1000,
            actionCode: '',
            buyerCode: '',
            beginTime: new Date(Date.now() - 1000 * 60),
            totalCount: (selectedItem['fieldNumber1'] || 0) + selectedItem['fieldNumber2'],
            fieldNumber1: selectedItem['fieldNumber1'],
            offset: selectedItem.id,
            keyApi: `${selectedItem.keyApi}_Copy`
          });
        });
      } else {
        ToastService.error(localized('validation.positiveDifferenceOnly'));
      }
    } else {
      ToastService.error(localized('validation.selectTransaction'));
    }
  }

  const showCancelLog = () => {
    const cancelItems = [];
    if (Array.isArray(selectedItems)) {
      cancelItems.push(...selectedItems);
    } else if (selectedItems?.['id']) {
      cancelItems.push(selectedItems);
    }
    for (const cancelItem of cancelItems) {
      if (cancelItem.invoiceId) {
        return ToastService.error(localized('validation.cannotCancelInvoiced'));
      }
      if (cancelItem.status === 3) {
        return ToastService.error(localized('validation.alreadyCancelled'));
      }
    }
    if (cancelItems.length) {
      createCancelLog({ items: cancelItems.map(({ id }) => id) });
    } else {
      ToastService.error(localized('validation.selectTransaction'));
    }
  }

  const cloneItem = (item) => {
    if (item && item[dataKey]) {
      Service.get(item[dataKey]).then(data => {
        data.saleDate = new Date();
        if (data.price > 0) {
          data.listPrice = data.price;
        }
        if (data.ratioTax > 0 && data.price > 0) {
          data.listPrice = Math.round(data.price * (1 + data.ratioTax / 100));
        }
        data.storeCode = storeCodeOptions.find(it => it.code === data.storeCode);
        clone(data);
      });

      setIsFormCreate(false);
    } else {
      ToastService.error(localized('validation.selectTransaction'));
    }
  }

  const deleteItem = (item) => {
    if (item) {
      ConfirmDialogService.confirmDelete(async () => {
        if (Array.isArray(item)) {
          for (const it of item) {
            await Service.delete(it[dataKey]);
          }
          ToastService.success();
          reloadLazyData();
        } else {
          Service.delete(item[dataKey]).then(() => {
            ToastService.success();
            reloadLazyData();
          });
        }
      });

      setIsFormCreate(false);
    } else {
      ToastService.error(localized('validation.selectTransaction'));
    }
  }
  const { renderImExExcel, download, showImportDialog } = useImExExcel({
    exportTemplate: () => {
      return Service.downloadExcelTemplate();
    },
    exportExcel: (params) => {
      return Service.downloadExcel(params);
    },
    exportExcelFileName: 'danh-sach-giao-dich-xang-dau',
    importExcel: (data) => {
      return Service.uploadExcel(data);
    },
    reloadLazyData
  });

  const { renderToolbar, refreshToolbar } = useToolbar({
    dataKey,
    showImportDialog,
    reloadLazyData, selectedItems,
    otherActions: [
      {
        label: t('report.createInvoice'), icon: 'pi pi-fw pi-pencil', className: 'iam-toolbar iam-toolbar-help', command: createInvoice
      },
      {
        label: t('common.export'), icon: 'pi pi-fw pi-th-large', className: 'iam-toolbar iam-toolbar-help', command: () => setDisplayExportExcel(true)
      },
      {
        label: t('common.print') + ' log', icon: 'pi pi-fw pi-th-large', className: 'iam-toolbar iam-toolbar-help', command: printLog
      },
      {
        label: t('common.updateInvoiceNumber'), icon: 'pi pi-fw pi-replay', className: 'iam-toolbar iam-toolbar-help', command: updateInvoiceNo
      },
      {
        label: t('common.cancel') + ' ' + t('common.transaction'), icon: 'pi pi-fw pi-sort-alt-slash', className: 'iam-toolbar iam-toolbar-help', command: showCancelLog
      },
      {
        label: t('common.compensateTransaction'), icon: 'pi pi-fw pi-plus', className: 'iam-toolbar iam-toolbar-help', command: createCompensation
      },
      {
        label: t('common.qrCodePayment'), icon: 'pi pi-fw pi-plus', className: 'iam-toolbar iam-toolbar-help', command: async (item) => {
          const printItems = [];
          if (item?.id) {
            printItems.push(item['id']);
          } else if (Array.isArray(selectedItems)) {
            printItems.push(...selectedItems.map(({ id }) => id));
          } else if (selectedItems?.['id']) {
            printItems.push(selectedItems['id']);
          }
          if (!printItems.length) {
            return ToastService.error(localized('validation.selectTransaction'));
          }

          const isQrPaymentEnabled = await fetchPetrolQrPaymentEnabled();
          if (!isQrPaymentEnabled) {
            return ToastService.error(PETROL_QR_PAYMENT_NOT_REGISTERED_MESSAGE);
          }

          setDisplayQrCode(true);
        }
      }
    ],
    endMenu: <Fragment>
      {!AppStorage.get('UserName')?.toLowerCase().includes('admin') && <Fragment>
        <Button icon="pi pi-ellipsis-h" className="p-button-rounded p-button-outlined p-button-secondary" onClick={e => op.current.toggle(e)} />
        <OverlayPanel ref={op} style={{ width: '160px', fontSize: '15px' }}>
          <div className="grid" onClick={e => op.current.hide()}>
            <div className="col-12 pointer" onClick={doCreate}>
              <i className="pi pi-fw pi-plus"></i> {t('common.add')}
            </div>
            <div className="col-12 pointer" onClick={() => updateItem(selectedItems)}>
              <i className="pi pi-fw pi-pencil"></i> {t('common.edit')}
            </div>
            <div className="col-12 pointer" onClick={() => deleteItem(selectedItems)}>
              <i className="pi pi-fw pi-trash"></i> {t('common.delete')}
            </div>
            <div className="col-12 pointer" onClick={() => cloneItem(selectedItems)}>
              <i className="pi pi-fw pi-copy"></i> {t('common.copy')}
            </div>
            <div className="col-12 pointer" onClick={createGroupLog}>
              <i className="pi pi-fw pi-copy"></i> {t('common.groupTransactions')}
            </div>
          </div>
        </OverlayPanel>
      </Fragment>}
      {configButton}
    </Fragment>
  });

  const onAddSelectedItems = (items) => {
    let InvoiceIdArray;
    if (Array.isArray(items) && items.length) {
      InvoiceIdArray = items.map(({ id }) => id);
    }

    if (!InvoiceIdArray || InvoiceIdArray.length < 1) {
      ConfirmDialogService.showInvalid(t('validation.selectDevice'));
      return;
    }
  }

  const onSubmitEnd = (result, removeReturnInvoiceScreen) => {
    if (removeReturnInvoiceScreen === true) {
      setReturnInvoiceScreen(null);
    }
    if (selectedItems?.['id']) {
      if (result?.invoiceSerial) {
        Service.putInvoice(selectedItems['id'], {
          invoiceId: result.id,
          invoiceSerial: result.invoiceSerial,
          invoiceNo: result.invoiceNumber,
          registerInvoiceId: result.registerInvoiceId,
          errorMessage: ''
        }).then(() => {
          refreshLazyData();
        });
      } else {
        if (result?.response?.data?.error?.message) {
          // Service.putInvoice(selectedItems['id'], {
          //   errorMessage: result?.response?.data?.error?.message
          // }).then(() => {
          //   refreshLazyData();
          // });
        }
      }
    }
  }

  const { render: renderDialogCancelLog, create: createCancelLog } = useDialogCrup({
    header: '!' + t('common.cancel') + ' ' + t('common.transaction'),
    dataKey,
    width: '45rem',
    fields: [
      { field: 'items', type: 'hidden' },
      { field: 'reason', header: t('common.cancelReason'), required: true, className: 'md:col-12' }
    ],
    createItem: item => {
      return Service.createCancelLog(item);
    },
    refreshLazyData
  });

  const { renderSign, sign } = useSign({ onComplete: refreshLazyData });

  return (
    <Fragment>
      {renderToolbar()}
      {renderDataTable()}
      {renderDialogCrup()}
      {renderDialogCancelLog()}
      {renderImExExcel()}
      <DialogSendEmail display={displaySendEmail} setDisplay={setDisplaySendEmail} sendType={1} selectedItems={viewItem} />
      <DialogView display={displayView} setDisplay={setDisplayView} selectedItem={viewItem} setDisplaySendEmail={setDisplaySendEmail}
        sign={sign} />
      <DialogCrupXangDau display={displayCrupXangDau} setDisplay={setDisplayCrupXangDau} view={view}
        currencies={currencies} paymentTypes={paymentTypes} registerInvoice={registerInvoice}
        selectedItems={createDefaultValue}
        refreshLazyData={() => {
        }} sign={sign} onSubmitEnd={onSubmitEnd} />
      <DialogQrCode display={displayQrCode} setDisplay={setDisplayQrCode} selectedItems={selectedItems} />
      <DialogInvoicePaymentHistory
        display={displayPaymentHistory}
        setDisplay={setDisplayPaymentHistory}
        sourceType="fuel_log"
        selectedItem={paymentHistoryItem}
      />
      {renderSign()}

      <ExportExcel display={displayExportExcel} setDisplay={setDisplayExportExcel} />
    </Fragment>
  )
}
